import { Typography } from '@mui/material';
import { CORE_FEE_TEMPLATES } from 'adminConstants/routes';
import Heading from 'components/Heading';
import PageContainer from 'components/PageContainer';
import PageContent from 'components/PageContent';
import PageLoader from 'components/PageLoader';
import AddEditCoreFeeTemplateForm from './AddEditCoreFeeTemplateForm';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { coreFeeTemplates as rules } from 'further-ui/validations';
import api from 'lib/trpcClient';
import { Api } from 'further-types/core-fee';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { useHandleTrpcQuery } from 'hooks/ui/useHandleTrpcQuery';
import {
  FeeType,
  FeeCalculationBasis,
  FeeChargingFrequency,
  FeeAccounting,
  CoreFeeType,
} from 'further-types/investment';
import { useParams } from 'react-router-dom';
import { useEffect } from 'react';
import lookupDisplayFeeType from 'utils/lookup-display-fee-type';

export type FeeTemplateFormData = {
  id?: string;
  firmId?: string;
  feeTemplateName: string;
  templateInUse: boolean;
  coreFees: Array<{
    feeName: string;
    feeType: FeeType;
    displayFeeType: CoreFeeType;
    feeCalculationBasis: FeeCalculationBasis | null;
    minimumFeePercentage: number | null;
    netFee: number | null;
    grossFee: number;
    vat: number;
    chargingFrequency?: FeeChargingFrequency | null;
    startingPeriod?: number | null;
    finishingPeriod?: number | null;
    feeAccounting: FeeAccounting;
  }>;
};

const AddEditCoreFeeTemplate: React.FC = () => {
  const { id } = useParams<{ id: string }>();

  const breadcrumbs = [
    { label: 'Dashboard' },
    { label: 'Core fee templates', link: CORE_FEE_TEMPLATES },
  ];

  const {
    data: coreFeeTemplate,
    isLoading,
    error,
  } = api.coreFeeTemplates.getById.useQuery({ id }, { enabled: !!id });

  useHandleTrpcQuery({ error, errorRedirect: CORE_FEE_TEMPLATES });

  const values: FeeTemplateFormData = {
    feeTemplateName: '',
    firmId: '',
    coreFees: [],
    templateInUse: false,
  };

  const formMethods = useForm({
    shouldFocusError: true,
    resolver: zodResolver(rules.CreateOrEditCoreFeeTemplateInput),
    criteriaMode: 'all',
    values,
    mode: 'onChange',
  });

  useEffect(() => {
    if (coreFeeTemplate) {
      const values = {
        feeTemplateName: coreFeeTemplate.name,
        firmId: coreFeeTemplate.firmId,
        coreFees: coreFeeTemplate.coreFees.map((fee) => ({
          ...fee,
          displayFeeType: lookupDisplayFeeType(
            fee.feeType,
            fee.feeCalculationBasis,
          ),
        })),
        templateInUse: coreFeeTemplate.templateInUse,
      };

      formMethods.reset(values);
    }
  }, [coreFeeTemplate, formMethods.reset]);

  const {
    mutate: createCoreFeeTemplate,
    isPending: createCoreFeeTemplatePending,
  } = api.coreFeeTemplates.create.useMutation(
    useHandleTrpcMutation()({
      successMessage: 'Core fee template created successfully.',
      successRedirect: CORE_FEE_TEMPLATES,
    }),
  );

  const {
    mutate: updateCoreFeeTemplate,
    isPending: updateCoreFeeTemplatePending,
  } = api.coreFeeTemplates.update.useMutation(
    useHandleTrpcMutation()({
      successMessage: 'Core fee template updated successfully.',
      successRedirect: CORE_FEE_TEMPLATES,
    }),
  );

  const saveFeeTemplate = formMethods.handleSubmit(
    (data: Api.CreateOrEditCoreFeeTemplateRequest) => {
      if (isEdit) {
        updateCoreFeeTemplate({
          ...data,
          id: coreFeeTemplate?._id,
          nameOnlyUpdate: coreFeeTemplate?.templateInUse,
          coreFees: data.coreFees.map((fee) => ({
            ...fee,
            netFee: fee.netFee ?? 0,
          })),
        });
      } else {
        createCoreFeeTemplate({
          ...data,
          coreFees: data.coreFees.map((fee) => ({
            ...fee,
            netFee: fee.netFee ?? 0,
          })),
        });
      }
    },
  );

  const isEdit = !!coreFeeTemplate;

  if (isLoading) {
    return <PageLoader />;
  }

  return (
    <PageContainer
      heading={`Fee manager: ${isEdit ? 'Edit' : 'Add'} core fee template`}
      breadcrumbs={breadcrumbs}
    >
      <PageContent withSpacing>
        <Heading variant="h2" noMargin>
          {isEdit ? 'Edit' : 'Add'} core fee template
        </Heading>
        <Typography variant="body2" color="text.secondary">
          Use this section to {isEdit ? 'edit an existing' : 'add a new'} core
          fee template, which can be applied to a tranche by default or to
          individual investments.
        </Typography>

        {(!id || coreFeeTemplate) && (
          <FormProvider {...formMethods}>
            <form onSubmit={saveFeeTemplate}>
              <AddEditCoreFeeTemplateForm
                isLoading={
                  createCoreFeeTemplatePending || updateCoreFeeTemplatePending
                }
                templateInUse={coreFeeTemplate?.templateInUse}
                isEdit={isEdit}
              />
            </form>
          </FormProvider>
        )}
      </PageContent>
    </PageContainer>
  );
};

export default AddEditCoreFeeTemplate;
